import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { ClientDto } from '../../../../core/models/clients/client.model';
import { ClientService } from '../../../../core/services/client.service';
import { CaseService } from '../../../../core/services/case.service';
import { ExpenseService } from '../../../../core/services/expense.service';
import { TransactionService } from '../../../../core/services/transaction.service';
import { DiaryService } from '../../../../core/services/diary.service';
import { NotificationService } from '../../../../shared/components/notifications/notification.service';
import { DeleteConfirmDialogComponent } from '../../../../shared/components/delete-confirm-dialog/delete-confirm-dialog.component';
import { AttachmentsModalComponent } from '../../../../shared/components/attachments-modal/attachments-modal.component';
import { DiaryFormModalComponent } from '../../../../shared/components/diary-form-modal/diary-form-modal.component';
import { FileUploaderComponent } from '../../../../shared/components/file-uploader/file-uploader.component';
import { ClientNotesModalComponent, ClientNotesModalData } from '../../../../shared/components/client-notes-modal/client-notes-modal.component';
import { CaseNotesModalComponent, CaseNotesModalData } from '../../../../shared/components/case-notes-modal/case-notes-modal.component';
import { AttachmentForCreation } from '../../../../core/models/attachments/attachment-for-creation.model';
import { AttachmentsService } from '../../../../core/services/attachments.service';
import { ViewChild } from '@angular/core';

import { CaseForListing } from '../../../../core/models/case/case-for-listing.model';
import { ExpenseForListing } from '../../../../core/models/expenses/expense-for-listing.model';
import { TransactionForListing } from '../../../../core/models/transactions/transaction-for-listing.model';
import { DiaryForListing } from '../../../../core/models/diaries/diary-for-listing.model';
import { AttachmentOverviewDto2 } from '../../../../core/models/attachments/attachment-overview-2.model';

import { CaseRequestFilter } from '../../../../core/models/case/case-request-filter.model';
import { ExpenseRequestFilter } from '../../../../core/models/expenses/expense-request-filter.model';
import { TransactionsRequestFilter } from '../../../../core/models/transactions/transaction-request-filter.model';
import { DiaryRequestFilter } from '../../../../core/models/diaries/diary-request-filter.model';

@Component({
  selector: 'app-client-details',
  standalone: false,
  templateUrl: './client-details.component.html',
  styleUrls: ['./client-details.component.css']
})
export class ClientDetailsComponent implements OnInit {
  clientId!: number;
  client: ClientDto | null = null;
  isLoading = false;
  activeTab: string = 'cases';
  selectedIndex: number = 0;
  // Cases tab
  cases: CaseForListing[] = [];
  casesLoading = false;
  casesCurrentPage = 0;
  casesPageSize = 10;
  casesTotalItems = 0;

  // Expenses tab
  expenses: ExpenseForListing[] = [];
  expensesLoading = false;
  expensesCurrentPage = 0;
  expensesPageSize = 10;
  expensesTotalItems = 0;

  // Transactions tab
  transactions: TransactionForListing[] = [];
  transactionsLoading = false;
  transactionsCurrentPage = 0;
  transactionsPageSize = 10;
  transactionsTotalItems = 0;

  // Diaries for cases
  caseDiaries: { [caseId: number]: DiaryForListing[] } = {};

  // Pagination for diaries in cases tab
  caseDiariesCurrentPage: { [caseId: number]: number } = {};
  caseDiariesPageSize: { [caseId: number]: number } = {};
  caseDiariesTotalItems: { [caseId: number]: number } = {};

  // Attachments tab properties
  attachmentSearchTerm: string = '';
  showUploader = false;
  isSaving = false;
  @ViewChild(FileUploaderComponent) fileUploader!: FileUploaderComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private clientService: ClientService,
    private caseService: CaseService,
    private expenseService: ExpenseService,
    private transactionService: TransactionService,
    private diaryService: DiaryService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private attachmentsService: AttachmentsService
  ) { }

  ngOnInit(): void {
    this.clientId = Number(this.route.snapshot.paramMap.get('id'));
    if (this.clientId) {
      this.loadClientDetails();
      // Load all tabs on page load
      this.loadCases();
      this.loadExpenses();
      this.loadTransactions();
      this.loadClientAttachments();
    }
    this.route.queryParams.subscribe(params => {
      if (params['activeTab']) {
        this.activeTab = params['activeTab'];
      }

      const tabMap: { [key: string]: number } = {
        cases: 0,
        expenses: 2,
        transactions: 3
      };

      setTimeout(() => {
        this.selectedIndex = tabMap[this.activeTab] ?? 0;
      });
    });
  }

  loadClientDetails(): void {
    this.isLoading = true;
    this.clientService.getClientById(this.clientId).subscribe({
      next: (client) => {
        this.client = client;
        this.isLoading = false;
      },
      error: () => {
        this.notificationService.showError('Failed to load client details');
        this.isLoading = false;
      }
    });
  }

  // Cases tab methods
  loadCases(): void {
    this.casesLoading = true;
    const apiPage = this.casesCurrentPage + 1;

    const filter: CaseRequestFilter = {
      page: apiPage,
      pageSize: this.casesPageSize,
      clientId: this.clientId
    };

    this.caseService.getPaginatedCases(filter).subscribe({
      next: ({ data, pagination }) => {
        this.cases = data;
        this.casesTotalItems = pagination.totalItems;
        this.casesPageSize = pagination.itemsPerPage;
        this.casesCurrentPage = pagination.currentPage - 1;
        this.casesLoading = false;

        // Load diaries for each case
        this.cases.forEach(caseItem => {
          this.loadCaseDiaries(caseItem.id);
        });
      },
      error: () => {
        this.notificationService.showError('Failed to load cases');
        this.casesLoading = false;
      }
    });
  }

  loadCaseDiaries(caseId: number): void {
    const currentPage = this.caseDiariesCurrentPage[caseId] || 0;
    const pageSize = this.caseDiariesPageSize[caseId] || 5;
    const apiPage = currentPage + 1;

    const filter: DiaryRequestFilter = {
      page: apiPage,
      pageSize: pageSize,
      caseId: caseId
    };

    this.diaryService.getPaginatedDiaries(filter).subscribe({
      next: ({ data, pagination }) => {
        this.caseDiaries[caseId] = data;
        this.caseDiariesTotalItems[caseId] = pagination.totalItems;
        this.caseDiariesPageSize[caseId] = pagination.itemsPerPage;
        this.caseDiariesCurrentPage[caseId] = pagination.currentPage - 1;
      },
      error: () => {
        this.caseDiaries[caseId] = [];
        this.caseDiariesTotalItems[caseId] = 0;
      }
    });
  }

  onCasesPageChange(event: any): void {
    this.casesCurrentPage = event.pageIndex;
    this.casesPageSize = event.pageSize;
    this.loadCases();
  }

  // Expenses tab methods
  loadExpenses(): void {
    this.expensesLoading = true;
    const apiPage = this.expensesCurrentPage + 1;

    const filter: ExpenseRequestFilter = {
      page: apiPage,
      pageSize: this.expensesPageSize,
      clientId: this.clientId
    };

    this.expenseService.getPaginatedExpenses(filter).subscribe({
      next: ({ data, pagination }) => {
        this.expenses = data;
        this.expensesTotalItems = pagination.totalItems;
        this.expensesPageSize = pagination.itemsPerPage;
        this.expensesCurrentPage = pagination.currentPage - 1;
        this.expensesLoading = false;
      },
      error: () => {
        this.notificationService.showError('Failed to load expenses');
        this.expensesLoading = false;
      }
    });
  }

  onExpensesPageChange(event: any): void {
    this.expensesCurrentPage = event.pageIndex;
    this.expensesPageSize = event.pageSize;
    this.loadExpenses();
  }

  // Transactions tab methods
  loadTransactions(): void {
    this.transactionsLoading = true;
    const apiPage = this.transactionsCurrentPage + 1;

    const filter: TransactionsRequestFilter = {
      page: apiPage,
      pageSize: this.transactionsPageSize,
      clientId: this.clientId
    };

    this.transactionService.getPaginatedTransactions(filter).subscribe({
      next: ({ data, pagination }) => {
        this.transactions = data;
        this.transactionsTotalItems = pagination.totalItems;
        this.transactionsPageSize = pagination.itemsPerPage;
        this.transactionsCurrentPage = pagination.currentPage - 1;
        this.transactionsLoading = false;
      },
      error: () => {
        this.notificationService.showError('Failed to load transactions');
        this.transactionsLoading = false;
      }
    });
  }

  onTransactionsPageChange(event: any): void {
    this.transactionsCurrentPage = event.pageIndex;
    this.transactionsPageSize = event.pageSize;
    this.loadTransactions();
  }

  // Tab change handler - no longer needed as all tabs load on page load
  onTabChange(event: any): void {
    // All tabs are now loaded on page load, no refresh needed
  }

  // Action methods
  editClient(): void {
    const currentUrl = this.router.url;
    this.router.navigate(['/clients/edit', this.clientId], {
      state: { returnUrl: currentUrl }
    });
  }

  deleteCase(caseId: number): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Delete Case', message: 'Are you sure you want to delete this case?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.caseService.softDeleteCase(caseId).subscribe({
          next: () => {
            this.notificationService.showSuccess('Case deleted successfully');
            this.loadCases();
          },
          error: () => {
            this.notificationService.showError('Failed to delete case');
          }
        });
      }
    });
  }

  editCase(caseId: number): void {
    const currentUrl = this.router.url;
    this.router.navigate(['/cases/edit', caseId], {
      state: { returnUrl: currentUrl }
    });
  }

  deleteDiary(diaryId: number): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Delete Diary', message: 'Are you sure you want to delete this diary entry?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.diaryService.softDeleteDiary(diaryId).subscribe({
          next: () => {
            this.notificationService.showSuccess('Diary deleted successfully');
            this.loadCases(); // Reload to refresh diaries
          },
          error: () => {
            this.notificationService.showError('Failed to delete diary');
          }
        });
      }
    });
  }

  editDiary(diaryId: number): void {
    const dialogRef = this.dialog.open(DiaryFormModalComponent, {
      width: '800px',
      data: {
        diaryId: diaryId,
        clientId: this.clientId,
        cases: this.cases
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refresh all case diaries to show updated diary
        this.cases.forEach(caseItem => {
          this.loadCaseDiaries(caseItem.id);
        });
      }
    });
  }

  deleteExpense(expenseId: number): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Delete Expense', message: 'Are you sure you want to delete this expense?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.expenseService.softDeleteExpense(expenseId).subscribe({
          next: () => {
            this.notificationService.showSuccess('Expense deleted successfully');
            this.loadExpenses();
          },
          error: () => {
            this.notificationService.showError('Failed to delete expense');
          }
        });
      }
    });
  }

  editExpense(expenseId: number): void {
    const currentUrl = this.router.url;
    this.router.navigate(['/expenses/edit', expenseId], {
      state: { returnUrl: currentUrl }
    });
  }

  deleteTransaction(transactionId: number): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Delete Transaction', message: 'Are you sure you want to delete this transaction?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.transactionService.softDeleteTransaction(transactionId).subscribe({
          next: () => {
            this.notificationService.showSuccess('Transaction deleted successfully');
            this.loadTransactions();
          },
          error: () => {
            this.notificationService.showError('Failed to delete transaction');
          }
        });
      }
    });
  }

  editTransaction(transactionId: number): void {
    const currentUrl = this.router.url;
    this.router.navigate(['/transactions/edit', transactionId], {
      state: { returnUrl: currentUrl }
    });
  }

  // Open attachments dialog for case (within client details Cases tab)
  openCaseAttachmentsModal(attachments: AttachmentOverviewDto2[], caseItem: CaseForListing): void {
    const dialogRef = this.dialog.open(AttachmentsModalComponent, {
      width: '800px',
      data: attachments
    });

    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.caseId = caseItem.id;
      dialogRef.componentInstance.attachmentType = 'Case';
    }

    dialogRef.afterClosed().subscribe(() => {
      const modalInstance = dialogRef.componentInstance;
      if (modalInstance && modalInstance.attachments) {
        caseItem.attachments = modalInstance.attachments;
      }
    });
  }

  openAttachmentsModal(): void {
    if (!this.client?.attachments) return;

    const dialogRef = this.dialog.open(AttachmentsModalComponent, {
      width: '800px',
      data: this.client.attachments
    });

    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.clientId = this.clientId;
      dialogRef.componentInstance.attachmentType = 'Client';
    }

    dialogRef.afterClosed().subscribe(() => {
      // Refresh client data to get updated attachments
      this.loadClientDetails();
    });
  }

  // New methods for adding records
  addNewCase(): void {
    const currentUrl = this.router.url;
    this.router.navigate(['/cases/create'], { 
      queryParams: { clientId: this.clientId },
      state: { returnUrl: currentUrl }
    });
  }

  addNewDiary(caseId: number): void {
    const dialogRef = this.dialog.open(DiaryFormModalComponent, {
      width: '800px',
      data: {
        caseId: caseId,
        clientId: this.clientId,
        cases: this.cases
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refresh the diaries for this case
        this.loadCaseDiaries(caseId);
      }
    });
  }

  addNewExpense(): void {
    const currentUrl = this.router.url;
    this.router.navigate(['/expenses/create'], { 
      queryParams: { clientId: this.clientId },
      state: { returnUrl: currentUrl }
    });
  }

  addNewTransaction(): void {
    const currentUrl = this.router.url;
    this.router.navigate(['/transactions/create'], { 
      queryParams: { clientId: this.clientId },
      state: { returnUrl: currentUrl }
    });
  }

  // Handle diary pagination in cases tab
  onCaseDiaryPageChange(caseId: number, event: any): void {
    this.caseDiariesCurrentPage[caseId] = event.pageIndex;
    this.caseDiariesPageSize[caseId] = event.pageSize;
    this.loadCaseDiaries(caseId);
  }

  // Open attachments dialog for diary
  openDiaryAttachmentsModal(attachments: any[], diary: any): void {
    const dialogRef = this.dialog.open(AttachmentsModalComponent, {
      width: '800px',
      data: attachments
    });

    if (dialogRef.componentInstance) {
      dialogRef.componentInstance.diaryId = diary.id;
      dialogRef.componentInstance.attachmentType = 'Diary';
    }

    dialogRef.afterClosed().subscribe(() => {
      const modalInstance = dialogRef.componentInstance;
      if (modalInstance && modalInstance.attachments) {
        diary.attachments = modalInstance.attachments;
      }
    });
  }

  // Attachments tab methods
  loadClientAttachments(): void {
    // Attachments are loaded with client details
  }

  get filteredClientAttachments(): AttachmentOverviewDto2[] {
    if (!this.client?.attachments) return [];
    if (!this.attachmentSearchTerm) return this.client.attachments;
    return this.client.attachments.filter(attachment =>
      attachment.blob.name.toLowerCase().includes(this.attachmentSearchTerm.toLowerCase()) ||
      (attachment.notes && attachment.notes.toLowerCase().includes(this.attachmentSearchTerm.toLowerCase()))
    );
  }

  clearAttachmentSearch(): void {
    this.attachmentSearchTerm = '';
  }

  onUploadFilesClick(): void {
    this.showUploader = true;
  }

  async onSaveFiles(): Promise<void> {
    if (!this.fileUploader) return;

    this.isSaving = true;
    try {
      const uploadedBlobs = await this.fileUploader.uploadAllFiles();
      if (uploadedBlobs.length === 0) {
        this.notificationService.showError('Please select files to upload');
        return;
      }

      // Create attachments for each uploaded blob
      for (const blob of uploadedBlobs) {
        const attachment: AttachmentForCreation = {
          blobId: blob.id,
          notes: '',
          clientId: this.clientId
        };
        await this.attachmentsService.createAttachment(attachment).toPromise();
      }

      this.notificationService.showSuccess('Files uploaded successfully');
      this.showUploader = false;
      this.loadClientDetails(); // Refresh to get new attachments
    } catch (error) {
      this.notificationService.showError('Failed to upload files');
    } finally {
      this.isSaving = false;
    }
  }

  viewAttachment(blob: any): void {
    // Implement view attachment logic
    window.open(blob.url, '_blank');
  }

  downloadAttachment(blob: any): void {
    // Implement download attachment logic
    const link = document.createElement('a');
    link.href = blob.url;
    link.download = blob.name;
    link.click();
  }

  confirmDeleteAttachment(attachment: any): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Delete Attachment', message: 'Are you sure you want to delete this attachment?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.attachmentsService.deleteAttachment(attachment.id).subscribe({
          next: () => {
            this.notificationService.showSuccess('Attachment deleted successfully');
            this.loadClientDetails(); // Refresh to get updated attachments
          },
          error: () => {
            this.notificationService.showError('Failed to delete attachment');
          }
        });
      }
    });
  }

  openClientNotesModal(): void {
    if (!this.client) return;

    const dialogData: ClientNotesModalData = {
      clientId: this.clientId,
      clientName: this.client.fullName
    };

    this.dialog.open(ClientNotesModalComponent, {
      width: '95vw',
      height: '95vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: dialogData,
      disableClose: false
    });
  }

  openCaseNotesModal(caseId: number, caseNumber?: string): void {
    const dialogData: CaseNotesModalData = {
      caseId: caseId,
      caseNumber: caseNumber
    };

    this.dialog.open(CaseNotesModalComponent, {
      width: '95vw',
      height: '95vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: dialogData,
      disableClose: false
    });
  }
}
